<template>
  <SidePanel
    :open="aiStore.isOpen"
    :title="t('ai.title')"
    show-close
    resizable
    fixed-header
    storage-key="ai-panel-width"
    :default-width="420"
    :min-width="320"
    :max-width="700"
    @update:open="aiStore.isOpen = $event"
    @close="aiStore.closePanel()"
  >
    <template #icon>
      <Sparkles class="size-4 text-primary" />
    </template>

    <template #rightAction>
      <Button
        variant="ghost"
        size="icon"
        class="size-7"
        :disabled="messages.length === 0"
        :title="t('ai.exportConversation')"
        @click="exportConversation"
      >
        <Download class="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="size-7"
        :title="t('ai.conversationHistory')"
        @click="toggleConversationList"
      >
        <History class="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="size-7"
        :title="t('ai.newConversation')"
        @click="aiStore.startNewConversation()"
      >
        <Plus class="size-4" />
      </Button>
    </template>

    <template #subtitle>
      <AiProviderSelect />
    </template>

    <!-- Conversation list view -->
    <AiConversationList v-if="aiStore.showConversationList" />

    <!-- Chat view -->
    <div
      v-else
      class="flex h-full flex-col overflow-hidden"
    >
      <!-- Messages area -->
      <div class="relative min-h-0 flex-1">
        <div
          ref="messagesContainer"
          class="h-full overflow-y-auto"
          @scroll="onScroll"
        >
          <!-- Empty state with suggested prompts -->
          <div
            v-if="messages.length === 0 && !isStreaming && !aiStore.isLoadingHistory"
            class="flex h-full flex-col items-center justify-center gap-4 p-6 text-center"
          >
            <div class="flex size-14 items-center justify-center rounded-full bg-primary/10">
              <Sparkles class="size-7 text-primary" />
            </div>
            <div>
              <p class="text-sm font-semibold">{{ t('ai.welcomeTitle') }}</p>
              <p class="mt-1 text-xs text-muted-foreground">{{ t('ai.welcomeSubtitle') }}</p>
            </div>
            <div class="grid w-full max-w-[320px] grid-cols-2 gap-2">
              <button
                v-for="prompt in suggestedPrompts"
                :key="prompt.labelKey"
                class="
                  flex flex-col items-center gap-1.5 rounded-lg border bg-background p-3 text-center
                  text-xs transition-all duration-150
                  hover:scale-[1.02] hover:bg-muted/60 hover:shadow-sm
                "
                @click="sendMessage(t(prompt.labelKey))"
              >
                <component
                  :is="prompt.icon"
                  class="size-4 text-muted-foreground"
                />
                <span class="line-clamp-2 text-muted-foreground">{{ t(prompt.labelKey) }}</span>
              </button>
            </div>
          </div>

          <!-- Loading history skeleton -->
          <div
            v-else-if="aiStore.isLoadingHistory"
            class="space-y-3 py-4"
          >
            <!-- Simulated user message (right) -->
            <div class="flex justify-end px-4">
              <Skeleton class="h-10 w-3/5 rounded-lg" />
            </div>
            <!-- Simulated assistant message (left) -->
            <div class="flex gap-2 px-4">
              <Skeleton class="size-6 shrink-0 rounded-full" />
              <div class="flex-1 space-y-2">
                <Skeleton class="h-4 w-full" />
                <Skeleton class="h-4 w-4/5" />
                <Skeleton class="h-4 w-2/3" />
              </div>
            </div>
            <!-- Simulated user message (right) -->
            <div class="flex justify-end px-4">
              <Skeleton class="h-8 w-2/5 rounded-lg" />
            </div>
            <!-- Simulated assistant message (left) -->
            <div class="flex gap-2 px-4">
              <Skeleton class="size-6 shrink-0 rounded-full" />
              <div class="flex-1 space-y-2">
                <Skeleton class="h-4 w-full" />
                <Skeleton class="h-4 w-3/5" />
              </div>
            </div>
          </div>

          <!-- Messages -->
          <TransitionGroup
            v-else
            tag="div"
            name="msg"
            class="space-y-1 py-2"
          >
            <AiMessage
              v-for="(msg, index) in messages"
              :key="msg.id"
              :message="msg"
              :is-actively-streaming="isStreaming && isLastAssistantMessage(msg)"
              :animation-delay="aiStore.justLoadedHistory ? index * 50 : 0"
              @copy="copyMessageToClipboard"
              @regenerate="regenerateLastResponse"
              @edit-resend="handleEditResend"
            />

            <!-- Thinking indicator when no assistant message exists yet -->
            <div
              v-if="isStreaming && streamingPhase === 'thinking' && !hasActiveAssistantMsg"
              key="__thinking__"
              class="flex gap-2 px-4 py-2"
            >
              <div
                class="
                  mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10
                "
              >
                <Sparkles class="size-3 animate-pulse text-primary" />
              </div>
              <div class="flex items-center gap-2 py-1 text-sm text-muted-foreground">
                <span class="thinking-dots flex gap-0.5">
                  <span class="size-1.5 rounded-full bg-current"></span>
                  <span class="size-1.5 rounded-full bg-current"></span>
                  <span class="size-1.5 rounded-full bg-current"></span>
                </span>
                <span>{{ t('ai.thinking') }}</span>
              </div>
            </div>
          </TransitionGroup>
        </div>

        <!-- Scroll to bottom FAB -->
        <Transition name="fab">
          <button
            v-if="isScrolledUp && messages.length > 0"
            class="
              absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5
              rounded-full border bg-background/95 px-3 py-1.5 text-xs font-medium shadow-lg
              backdrop-blur transition-colors
              hover:bg-muted
            "
            @click="scrollToBottom(true)"
          >
            <ArrowDown class="size-3.5" />
            <span v-if="newMessageCount">{{ newMessageCount }} {{ t('ai.newMessages') }}</span>
          </button>
        </Transition>
      </div>

      <!-- Streaming status bar -->
      <div
        v-if="isStreaming"
        class="
          flex items-center gap-2 border-t bg-muted/30 px-4 py-1.5 text-xs text-muted-foreground
        "
      >
        <Loader2 class="size-3 animate-spin" />
        <span>{{ phaseLabel }}</span>
      </div>

      <!-- Input area -->
      <AiInput
        ref="aiInput"
        :is-streaming="isStreaming"
        @send="sendMessage"
        @stop="stopStreaming"
      />
    </div>
  </SidePanel>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { ArrowDown, BarChart3, Bell, Building2, Download, History, Loader2, Plus, Sparkles, Thermometer } from 'lucide-vue-next'

import SidePanel from '@/components/SidePanel/index.vue'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { AiMessage as AiMessageType } from '@/stores/ai'
import { useAiStore } from '@/stores/ai'
import { copyTextToClipboard } from '@/utils/copyTextToClipboard'

import AiConversationList from './AiConversationList.vue'
import AiInput from './AiInput.vue'
import AiMessage from './AiMessage.vue'
import AiProviderSelect from './AiProviderSelect.vue'
import { useAiChat } from './useAiChat'

const aiStore = useAiStore()
const { t } = useI18n()
const {
  messages, isStreaming, streamingPhase,
  sendMessage, stopStreaming, regenerateLastResponse,
} = useAiChat()

const suggestedPrompts = [
  { labelKey: 'ai.promptEntities', icon: Building2 },
  { labelKey: 'ai.promptTelemetry', icon: Thermometer },
  { labelKey: 'ai.promptChart', icon: BarChart3 },
  { labelKey: 'ai.promptAlarms', icon: Bell },
]

const messagesContainer = ref<HTMLElement>()
const aiInput = ref<InstanceType<typeof AiInput>>()
const isScrolledUp = ref(false)
const newMessageCount = ref(0)

function toggleConversationList() {
  aiStore.showConversationList = !aiStore.showConversationList
}

function onScroll() {
  const el = messagesContainer.value
  if (!el) return
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 100
  isScrolledUp.value = !atBottom
  if (atBottom) newMessageCount.value = 0
}

function scrollToBottom(force = false) {
  if (isScrolledUp.value && !force) {
    newMessageCount.value++
    return
  }
  nextTick(() => {
    const el = messagesContainer.value
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: force ? 'smooth' : 'instant' })
    }
    isScrolledUp.value = false
    newMessageCount.value = 0
  })
}

function copyMessageToClipboard(content: string) {
  copyTextToClipboard(content)
}

function handleEditResend(content: string) {
  aiInput.value?.setInputText(content)
}

function exportConversation() {
  const date = new Date().toISOString().slice(0, 10)
  const lines: string[] = [`# AI Conversation`, `_${date}_`, '---', '']

  for (const msg of messages.value) {
    if (msg.role === 'user') {
      lines.push('## You', '', msg.content, '')
    } else if (msg.role === 'assistant') {
      lines.push('## Assistant', '', msg.content, '')
    } else if (msg.role === 'tool') {
      const result = (msg.toolResult ?? msg.content).slice(0, 500)
      lines.push(`> **Tool: ${msg.toolName ?? 'tool'}**`, `> ${result}`, '')
    }
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/markdown' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `ai-conversation-${date}.md`
  a.click()
  URL.revokeObjectURL(url)
}

// Check if a message is the last assistant message currently being streamed to
function isLastAssistantMessage(msg: AiMessageType): boolean {
  if (msg.role !== 'assistant') return false
  for (let i = messages.value.length - 1; i >= 0; i--) {
    if (messages.value[i].role === 'assistant') return messages.value[i] === msg
  }
  return false
}

const hasActiveAssistantMsg = computed(() => messages.value.some(m => m.role === 'assistant'))

const phaseLabel = computed(() => {
  switch (streamingPhase.value) {
  case 'thinking':
    return t('ai.phaseThinking')
  case 'tool_calling':
    return t('ai.phaseToolCalling')
  case 'generating':
    return t('ai.phaseGenerating')
  default:
    return t('ai.phaseThinking')
  }
})

// Scroll to bottom when new messages arrive
watch(
  () => messages.value.length,
  () => scrollToBottom(),
)

// Scroll when streaming content updates
watch(
  () => messages.value.at(-1)?.content,
  () => scrollToBottom(),
)

// Focus input when panel opens
watch(
  () => aiStore.isOpen,
  (open) => {
    if (open) nextTick(() => aiInput.value?.focus())
  },
)
</script>

<style scoped>
/* Thinking dots animation — duplicated from AiMessage for the standalone indicator */
.thinking-dots span {
  animation: dot-bounce 1.4s ease-in-out infinite;
}

.thinking-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.thinking-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* TransitionGroup animations for messages */
.msg-enter-active {
  transition: all 0.3s ease-out;
}

.msg-leave-active {
  transition: all 0.2s ease-in;
}

.msg-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.msg-leave-to {
  opacity: 0;
}

/* Scroll-to-bottom FAB transition */
.fab-enter-active,
.fab-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fab-enter-from,
.fab-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}
</style>
